<script setup lang="ts">
import { PropType, ref } from "vue";
import { CrewMember } from "@/models/CrewMember";
import { useI18n } from "vue-i18n"; // Import the useI18n hook
import { useUserStore } from "@/stores/userStore"; // Import userStore

const { t } = useI18n(); // Initialize the i18n hook
const userStore = useUserStore(); // Use the userStore instance

const { member } = defineProps({
  member: {
    type: Object as PropType<CrewMember>,
    required: true
  },
})

const open = ref(false);
</script>

<template>
  <div class="bg-background-light rounded-8xl desktop">
    <div class="flex flex-row items-center invoice-row py-4 px-10 justify-between">
      <div class=" responsive-body-large font-bold flex flex-row items-center gap-3 mr-[20vw]">
        <div class="w-11 h-11 rounded-full border border-black/40"></div>
        {{ member.name }}
      </div>
      <div class="flex gap-5 items-center">
        <div class="flex gap-1.5">
          <img src="@/assets/icons/svg/Phone_16x16.svg" v-if="member.phone">
          {{ member.phone }}
        </div>
        <div class="flex flex-row items-center gap-1">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          {{ member.email }}
        </div>
        <div class="flex flex-row items-center">
          <div v-if="!member.passportNumber && !member.invitedByEmail"
            class="bg-notifications-actionRequired flex flex-row items-center px-4 py-1 gap-2 rounded-11xl">
            <img src="@/assets/icons/svg/Action_required_16x16.svg">
            {{ t('crew.incomplete') }}
          </div>
          <div v-else-if="member.invitedByEmail"
            class="bg-notifications-pending flex flex-row justify-center items-center px-4 py-1 gap-2 rounded-11xl">
            <img src="@/assets/icons/svg/Mail_16x16.svg">
            {{ t('crew.invitedByEmail') }}
          </div>
          <div v-else class="w-[12vw]"></div>
        </div>
      </div>
      <div @click="open = !open" class="cursor-pointer">
        <img src="@/assets/icons/svg/Up_16x16.svg" class="ml-8" v-if="open">
        <img src="@/assets/icons/svg/Down_16x16.svg" class="ml-8" v-else>
      </div>
      <div>
        <img src="@/assets/icons/svg/Menu dots_16x16.svg" class="ml-8">
      </div>
    </div>
    <div v-if="open" class="px-24 py-6">
      <table class="table-auto w-full text-left border-separate border-spacing-y-[6px]">
        <thead class="responsive-body-xsmall text-black/40 font-extralight">
          <tr>
            <th>{{ t('crew.nationality') }}</th>
            <th>{{ t('crew.dateOfBirth') }}</th>
            <th>{{ t('crew.passport') }}</th>
            <th>{{ t('crew.passportExpiry') }}</th>
            <th>{{ t('crew.sailingLicense') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ member.nationality }}</td>
            <td>{{ userStore.formatDate(new Date(member.dateOfBirth ?? new Date())) }}</td> <!-- Updated -->
            <td v-if="member.passportNumber">{{ member.passportNumber }}</td>
            <td v-else>
              <div
                class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </td>
            <td v-if="member.passportExpiry">{{ userStore.formatDate(new Date(member.passportExpiry)) }}</td>
            <!-- Updated -->
            <td v-else>
              <div
                class="bg-notifications-actionRequired flex flex-row justify-center items-center w-[107px] h-6 gap-2 rounded-11xl text-xs">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </td>
            <td class="flex flex-row items-start gap-1">
              {{ member.sailingLicense ? t('crew.yes') : t('crew.no') }}
              <img v-if="member.sailingLicense" src="@/assets/icons/svg/Download_16x16.svg" class="cursor-pointer">
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div class="bg-background-light rounded-8xl min-w-fit phone">
    <div class="flex flex-row items-center invoice-row py-4 px-5 justify-between">
      <div class=" responsive-body-medium font-bold flex flex-row items-center gap-3">
        <div class="w-11 h-11 rounded-full border border-black/40"></div>
        {{ member.name }}
      </div>
      <div class="flex">
        <div @click="open = !open" class="cursor-pointer">
          <img src="@/assets/icons/svg/Up_16x16.svg" class="ml-8" v-if="open">
          <img src="@/assets/icons/svg/Down_16x16.svg" class="ml-8" v-else>
        </div>
        <div>
          <img src="@/assets/icons/svg/Menu dots_16x16.svg" class="ml-8">
        </div>
      </div>
    </div>
    <div>
      <div class="px-5 py-4">
        <div class="flex flex-row items-center gap-1">
          <img src="@/assets/icons/svg/Phone_16x16.svg" v-if="member.phone">
          {{ member.phone }}
        </div>
        <div class="flex flex-row items-center gap-1">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          {{ member.email }}
        </div>
      </div>
      <div class="flex flex-row items-center p-4">
        <div v-if="!member.passportNumber && !member.invitedByEmail"
          class="bg-notifications-actionRequired flex flex-row items-center px-4 py-1 gap-2 rounded-11xl">
          <img src="@/assets/icons/svg/Action_required_16x16.svg">
          {{ t('crew.incomplete') }}
        </div>
        <div v-if="member.invitedByEmail"
          class="bg-notifications-pending flex flex-row items-center px-4 py-1 gap-2 rounded-11xl">
          <img src="@/assets/icons/svg/Mail_16x16.svg">
          {{ t('crew.invitedByEmail') }}
        </div>
      </div>
    </div>
    <div v-if="open" class="px-5 py-2.5">
      <div class="card-container">
        <div class="card">
          <div class="flex gap-10">
            <div class="card-section">
              <div>{{ t('crew.nationality') }}:</div>
              <div>{{ member.nationality }}</div>
            </div>
            <div class="card-section">
              <div>{{ t('crew.dateOfBirth') }}:</div>
              <div>{{ userStore.formatDate(new Date(member.dateOfBirth ?? new Date())) }}</div> <!-- Updated -->
            </div>
          </div>
          <div class="flex gap-10">
            <div class="card-section" v-if="member.passportNumber">
              <div>{{ t('crew.passport') }}:</div>
              <div>{{ member.passportNumber }}</div>
            </div>
            <div class="card-section" v-else>
              <div>{{ t('crew.passport') }}:</div>
              <div class="status-required">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </div>
            <div class="card-section" v-if="member.passportExpiry">
              <div>{{ t('crew.passportExpiry') }}:</div>
              <div>{{ userStore.formatDate(new Date(member.passportExpiry)) }}</div> <!-- Updated -->
            </div>
            <div class="card-section" v-else>
              <div>{{ t('crew.passportExpiry') }}:</div>
              <div class="status-required">
                <img src="@/assets/icons/svg/Action_required_16x16.svg">
                {{ t('crew.required') }}
              </div>
            </div>
          </div>
          <div class="card-section">
            <div>{{ t('crew.sailingLicense') }}:</div>
            <div class="flex align-center">
              {{ member.sailingLicense ? t('crew.yes') : t('crew.no') }}
              <img v-if="member.sailingLicense" src="@/assets/icons/svg/Download_16x16.svg" class="download-icon">
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
}

.card-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.card-section {
  margin-bottom: 8px;
  font-size: 14px;
  min-width: 50%;
}

.status-required {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffcca7;
  height: 25px;
  border-radius: 15px;
  font-size: 12px;
  gap: 8px;
}

.download-icon {
  cursor: pointer;
  margin-left: 4px;
}

strong {
  font-weight: bold;
}

@media (min-width: 768px) {
  .phone {
    display: none;
  }
}

@media (max-width: 768px) {
  .desktop {
    display: none;
  }
}
</style>
